import React, { useState, useRef, useEffect } from 'react';

import { convertToNormal, RemoveSpaces } from '@/utils/functions';
import { BlogTags } from '@/components/Blog/BlogArticlesInfo';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import * as styled from './Tag.styled';

export enum TagVariants {
  BIG = 'big',
  SMALL = 'small',
  BLOG_SMALL = 'blogSmall',
  DROPDOWN = 'dropdown',
}

export enum TagAttachment {
  CATEGORIES = 'Categories',
  TECHNOLOGIES = 'Technologies',
  FEATURES = 'Features',
  LANGUAGES = 'Languages',
  BLOG = 'Blog',
}

interface ITag {
  variant?: TagVariants;
  attachment?: TagAttachment;
  $active?: boolean;
  $fixed?: boolean;
  fixedTime?: number;
  $fixedTagBox?: boolean;
  restTags?: BlogTags[] | string[];
  currentTag?: string;
  itemProp?: string;
}

export const Tag: React.FC<ITag> = ({
  children,
  $active,
  variant = TagVariants.SMALL,
  attachment,
  $fixed,
  fixedTime,
  $fixedTagBox = false,
  restTags,
  currentTag,
  itemProp,
}) => {
  let tagHref: string;
  if (children === 'All' || children === convertToNormal(BlogTags.LATEST)) {
    if (children === 'All') {
      tagHref = '/templates';
    }
    if (children === convertToNormal(BlogTags.LATEST)) {
      tagHref = '/blog';
    }
  } else {
    if (attachment === TagAttachment.CATEGORIES) {
      tagHref = `/templates/category/${RemoveSpaces(children.toString())}`;
    }
    if (attachment === TagAttachment.TECHNOLOGIES) {
      tagHref = `/templates/technology/${RemoveSpaces(children.toString())}`;
    }
    if (attachment === TagAttachment.LANGUAGES) {
      tagHref = `/templates/language/${RemoveSpaces(children.toString())}`;
    }
    if (attachment === TagAttachment.FEATURES) {
      tagHref = `/templates/feature/${RemoveSpaces(children.toString())}`;
    }
    if (attachment === TagAttachment.BLOG) {
      tagHref = `/blog/tags/${RemoveSpaces(children.toString())}`;
    }
  }

  const [drowdownOpen, setDrowdownOpen] = useState<boolean>(false);

  const DrowdownTagContainerRef = useRef(null);

  useOnClickOutside(DrowdownTagContainerRef, () => setDrowdownOpen(false));

  const DrowdownListRef = useRef(null);

  const [restTagsLeft, setRestTagsLeft] = useState<number>(0);
  const [restTagsToCenter, setRestTagsToCenter] = useState<number>(0);

  useEffect(() => {
    if (DrowdownTagContainerRef?.current && DrowdownListRef?.current) {
      const left =
        DrowdownTagContainerRef.current.offsetWidth -
        DrowdownListRef.current.offsetWidth;
      setRestTagsLeft(left / 2);
      const toCenter =
        window.innerWidth / 2 -
        (DrowdownTagContainerRef.current.offsetLeft +
          DrowdownTagContainerRef.current.offsetWidth);
      setRestTagsToCenter(toCenter);
    }
  }, [DrowdownTagContainerRef?.current, DrowdownListRef?.current]);

  switch (variant) {
    case TagVariants.BIG:
      return (
        <styled.BigTagContainer
          $active={$active}
          $fixedTagBox={$fixedTagBox}
          $isFixed={$fixed}
          $fixedTime={fixedTime}
          to={tagHref}
        >
          <styled.BigText itemProp={itemProp}>{children}</styled.BigText>
        </styled.BigTagContainer>
      );
    case TagVariants.SMALL:
      return (
        <styled.SmallTagContainer to={tagHref}>
          <styled.SmallText itemProp={itemProp}>{children}</styled.SmallText>
        </styled.SmallTagContainer>
      );
    case TagVariants.BLOG_SMALL:
      return (
        <styled.SmallTagContainer to={tagHref} $isBlogArticleCard>
          <styled.SmallText itemProp={itemProp}>{children}</styled.SmallText>
        </styled.SmallTagContainer>
      );
    case TagVariants.DROPDOWN:
      useEffect(() => {
        if (!$fixed) {
          setDrowdownOpen(false);
        }
      }, [$fixed]);
      return (
        <styled.DrowdownTagContainer
          $active={$active}
          $fixedTagBox={$fixedTagBox}
          $isFixed={$fixed}
          $fixedTime={fixedTime}
          ref={DrowdownTagContainerRef}
          onClick={() => setDrowdownOpen(!drowdownOpen)}
        >
          <styled.BigText>{children}</styled.BigText>
          <styled.DrowdownRestTags
            open={drowdownOpen}
            length={restTags.length}
            $isFixed={$fixed}
            left={restTagsLeft}
            $mobileCenter={restTagsToCenter}
          >
            <styled.DrowdownList ref={DrowdownListRef}>
              {restTags?.map((tag: string) => {
                let href: string;
                if (attachment === TagAttachment.CATEGORIES) {
                  href = `/templates/category/${RemoveSpaces(tag)}`;
                }
                if (attachment === TagAttachment.TECHNOLOGIES) {
                  href = `/templates/technology/${RemoveSpaces(tag)}`;
                }
                if (attachment === TagAttachment.LANGUAGES) {
                  href = `/templates/language/${RemoveSpaces(tag)}`;
                }
                if (attachment === TagAttachment.FEATURES) {
                  href = `/templates/feature/${RemoveSpaces(tag)}`;
                }
                if (attachment === TagAttachment.BLOG) {
                  href = `/blog/tags/${RemoveSpaces(tag)}`;
                }
                return (
                  <styled.DrowdownListItem key={tag}>
                    <styled.BigTagContainer
                      $active={currentTag === RemoveSpaces(tag)}
                      $isFixed={$fixed}
                      $fixedTime={fixedTime}
                      to={href}
                    >
                      <styled.BigText>{tag}</styled.BigText>
                    </styled.BigTagContainer>
                  </styled.DrowdownListItem>
                );
              })}
            </styled.DrowdownList>
          </styled.DrowdownRestTags>
        </styled.DrowdownTagContainer>
      );
    default:
      return null;
  }
};
